import React, { useState, useMemo } from 'react';
import {
  Stack,
  Heading,
  Icon,
  FormControl,
  Textarea,
  Box,
} from '@chakra-ui/react';
import StepWrapper from '../StepWrapper';
import {
  FaDollarSign,
  FaClipboardList,
  FaStickyNote,
  FaCheckCircle,
  FaCheck,
} from 'react-icons/fa';
import { IoLocationOutline } from 'react-icons/io5';
import { ItemModel, ItemAskModel, UserModel, AddressModel } from 'models';
import { Condition } from 'models/types';
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai';
import { ItemAskStatus } from 'models/Item/ItemAsk/types';
import Wizard from '../Wizard';
import ProgressBar from '../../ProgressBar/ProgressBar';
import ItemBidForm, { ItemBidFormData } from '../../Form/ItemBidForm/ItemBidForm';
import { ItemBidAdditionalDataFormData } from '../../Form/ItemBidAdditionalDataForm/ItemBidAdditionalDataForm';
import ConditionDropdown from '../../ConditionDropdown/ConditionDropdown';
import ItemBidCard from '../../Item/ItemBidCard/ItemBidCard';
import PaymentBreakdown from '../../PaymentBreakdown/PaymentBreakdown';
import AddressDropdown from '../../AddressDropdown/AddressDropdown';

interface ItemBidWizardProps {
  user: UserModel;
  item: ItemModel;
  defaultBid?: ItemBidFormData;
  defaultAdditionalData?: ItemBidAdditionalDataFormData;
  onSubmit?: (data: ItemBidFormData, additionalData: ItemBidAdditionalDataFormData, useEscrow?: boolean) => void;
  isLoading?: boolean;
  hasErrors?: boolean;
  onAddAddressClick?: () => void;
}

export default function ItemBidWizard({
  user,
  item,
  onAddAddressClick,
  defaultBid = { bidExpiration: 30 },
  defaultAdditionalData,
  onSubmit = () => { },
  isLoading = false,
  hasErrors = false,
}: ItemBidWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [bidData, setBidData] = useState<ItemBidFormData>(defaultBid);
  const [condition, setCondition] = useState<Condition>(
    defaultAdditionalData?.condition || Condition.GOOD
  );
  const [notes, setNotes] = useState<string>(defaultAdditionalData?.notes || '');
  const [address, setAddress] = useState<AddressModel>(
    user.getPrimaryAddress() ||
    new AddressModel({
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    })
  );
  const [useEscrow, setUseEscrow] = useState(false);

  const opposingAskTransaction = useMemo(
    () =>
      new ItemAskModel({
        id: '',
        item_id: item.id,
        user_id: '',
        amount: bidData.bidPrice || 0,
        currency: 'USD',
        status: ItemAskStatus.PENDING,
        condition: condition,
        notes: notes,
        expiration_days: 30,
        verified: false,
      }),
    [item.id, bidData.bidPrice, condition, notes]
  );

  const onWizardSubmit = () => {
    onSubmit(bidData, { condition, notes, address, useEscrow });
  };

  const handleFormChange = (data: ItemBidFormData) => {
    setBidData(data);
  };

  const handleConditionChange = (condition: Condition) => {
    setCondition(condition);
  };

  const handleAddressChange = (updated: AddressModel) => {
    setAddress(updated);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  function renderBidPreview() {
    return (
      <ItemBidCard
        item={item}
        bidAmount={bidData.bidPrice || 0}
        bidCreatedDays={0}
        condition={condition}
        countryCode={address.country}
      />
    );
  }

  function renderAmountStep() {
    return (
      <Stack spacing={4}>
        {renderBidPreview()}
        <StepWrapper
          stepKey="amount"
          icon={FaDollarSign}
          title="Bid Amount"
          description="Set your bid price"
          minH="320px"
        >
          <ItemBidForm
            item={item}
            onChange={handleFormChange}
            defaultFormData={bidData}
            showSubmit={false}
            showItemCard={false}
            showExpirationDropdown={false}
          />
        </StepWrapper>
      </Stack>
    );
  }

  function renderConditionStep() {
    return (
      <Stack spacing={4}>
        {renderBidPreview()}
        <StepWrapper
          stepKey="condition"
          icon={FaClipboardList}
          title="Acceptable Condition"
          description="Select the minimum condition you will accept"
          minH="320px"
        >
          <ConditionDropdown
            defaultValue={condition}
            onChange={handleConditionChange}
            size="sm"
          />
        </StepWrapper>
      </Stack>
    );
  }

  function renderNotesStep() {
    return (
      <Stack spacing={4}>
        {renderBidPreview()}
        <StepWrapper
          stepKey="notes"
          icon={FaStickyNote}
          title="Add Notes"
          description="Additional details for sellers"
          minH="320px"
        >
          <FormControl>
            <Textarea
              value={notes}
              onChange={handleNotesChange}
              placeholder="Enter notes"
              size="sm"
            />
          </FormControl>
        </StepWrapper>
      </Stack>
    );
  }

  function renderAddressConfirmStep() {
    const userAddresses = [
      ...(user.getPrimaryAddress() ? [user.getPrimaryAddress() as AddressModel] : []),
      ...user.address.secondaryAddresses,
    ];

    return (
      <Stack spacing={4}>
        {renderBidPreview()}
        <StepWrapper
          stepKey="addressConfirm"
          icon={IoLocationOutline}
          title="Delivery Address"
          description="Confirm your delivery address"
          minH="320px"
        >
          <Stack gap={4} alignItems="center" width="100%">
            <AddressDropdown
              addresses={userAddresses}
              defaultAddress={address}
              onSelect={handleAddressChange}
              onAddAddress={onAddAddressClick}
              size="sm"
            />
          </Stack>
        </StepWrapper>
      </Stack>
    );
  }

  function renderConfirmationStep() {
    return (
      <Stack spacing={4}>
        {renderBidPreview()}
        <StepWrapper
          stepKey="confirm"
          icon={FaCheckCircle}
          title="Confirm Bid"
          minH="320px"
        >
          <Stack spacing={4}>
            <PaymentBreakdown
              transaction={opposingAskTransaction}
              type={"ask"}
              useEscrow={useEscrow}
              onToggleEscrow={setUseEscrow}
              showInfoGuide={false}
              showTransactionCard={false}
            />
          </Stack>
        </StepWrapper>
      </Stack>
    );
  }

  function renderWizardFinishingSuccessStep() {
    let content: JSX.Element;
    if (hasErrors) {
      content = (
        <Stack gap={4} alignItems={'center'}>
          <Icon as={AiFillCloseCircle} color={'red.500'} w={10} h={10} />
          <Heading size={'xs'} textAlign={'center'}>
            Your Bid Could Not Be Submitted.<br />Please Try Again
          </Heading>
        </Stack>
      );
    } else {
      content = (
        <Stack gap={4} alignItems={'center'}>
          <Icon as={AiFillCheckCircle} color={'green.500'} w={10} h={10} />
          <Heading size={'xs'} textAlign={'center'}>
            Your Bid Has Been Submitted
          </Heading>
        </Stack>
      );
    }

    return (
      <Box height={{ base: 'auto', md: 340 }}>
        {content}
      </Box>)
  }

  const wizardSteps = [
    {
      label: '',
      icon: FaDollarSign,
      content: renderAmountStep(),
      requiresValidation: true,
      isValidated: (bidData?.bidPrice || 0) > 0,
    },
    {
      label: '',
      icon: FaClipboardList,
      content: renderConditionStep(),
      requiresValidation: false,
      isValidated: true,
    },
    {
      label: '',
      icon: FaStickyNote,
      content: renderNotesStep(),
      requiresValidation: false,
      isValidated: true,
    },
    {
      label: '',
      icon: IoLocationOutline,
      content: renderAddressConfirmStep(),
      requiresValidation: true,
      isValidated: !!address.line1,
    },
    {
      label: '',
      icon: FaCheckCircle,
      content: renderConfirmationStep(),
      requiresValidation: true,
      isValidated: true,
    },
  ];

  const totalSteps = wizardSteps.length;
  const progressPercentage = ((activeStep + 1) / totalSteps) * 100;
  const progressIcon = activeStep === totalSteps - 1 ? FaCheck : wizardSteps[activeStep].icon;

  return (
    <Stack>
      <ProgressBar progress={progressPercentage} icon={progressIcon} dynamicColor height={4} />
      <Wizard
        steps={wizardSteps}
        finishStep={renderWizardFinishingSuccessStep()}
        loading={isLoading}
        showStepper={false}
        onStepChange={setActiveStep}
        onSubmit={onWizardSubmit}
      />
    </Stack>
  );
}
